// @flow

import React, { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { parseSchema } from "@utils/schema";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  NonLinearWizard,
  Step,
} from "@components";
import type { DynamicQuestionContent } from "@modules/service/reducer/dynamic-questions/types";
import type { HierarchyAssignment } from "@modules/service/types";
import * as serviceSelectors from "@modules/service/selectors";
import sharedActionCreators from "@modules/shared/actionCreator";
import actionCreators from "@modules/service/actionCreator";
import { QuestionTypeEnum } from "@modules/service/constants";
import Step1QuestionType from "./step1-question-type";
import Step2HierarchyScope from "./step2-hierarchy-scope";
import Step3ReasonSpecificUsage from "./step3-reason-specific-usages";
import Step4LengthScope from "./step4-length-scope";
import AnswerOptions from "./answer-options";

type PropsFromState = $Exact<{
  draft: ?DynamicQuestionContent,
  assignments: Array<HierarchyAssignment>,
  isAssignmentsValid: boolean,
  schema: ?Object,
}>;

type PropsFromDispatch = $Exact<{
  setDynamicQuestionRevisionSchema: typeof actionCreators.SetDynamicQuestionRevisionSchema,
  setShowAddDynamicQuestionModal: typeof actionCreators.SetShowAddDynamicQuestionModal,
  addNewDynamicQuestion: typeof actionCreators.AddNewDynamicQuestion,
  getAbsenceReasons: typeof sharedActionCreators.GetAbsenceReasons,
  getHierarchyTree: typeof sharedActionCreators.GetHierarchyTree,
}>;

type OwnProps = $Exact<{
  showModal: boolean,
  clientId: number,
  defaultStep: number,
  filter: string,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class AddDynamicQuestionWizardModal extends Component<Props> {
  componentDidMount = async () => {
    if (this.props.getAbsenceReasons) {
      this.props.getAbsenceReasons({ clientId: this.props.clientId });
    }

    this.props.getHierarchyTree({
      clientId: this.props.clientId,
    });

    const schema = await parseSchema("DynamicQuestionRevisionDTO", {
      clientId: this.props.clientId,
    });

    if (this.props.setDynamicQuestionRevisionSchema) {
      this.props.setDynamicQuestionRevisionSchema(schema);
    }
  };

  handleCancel = () => {
    this.props.setShowAddDynamicQuestionModal({ show: false, step: 1 });
  };

  handleSubmitDynamicQuestionDraft = () => {
    if (this.props.draft) {
      let draft: DynamicQuestionContent = this.props.draft;

      if (this.isAnswerOptionsHidden) {
        draft = {
          ...draft,
          selectableValues: [],
        };
      }

      this.props.addNewDynamicQuestion({
        draft,
        clientId: this.props.clientId,
        assignments: this.props.assignments,
        filter: this.props.filter,
      });
    }
  };

  get isAnswerOptionsHidden() {
    return (
      (this.props.draft &&
        (this.props.draft.questionType === QuestionTypeEnum.DATE_PICK ||
          this.props.draft.questionType === QuestionTypeEnum.FREE_TEXT)) ||
      false
    );
  }

  get totalSteps() {
    return this.isAnswerOptionsHidden ? 4 : 5;
  }

  render() {
    if (!this.props.schema) {
      return null;
    }

    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.handleCancel}>
          {this.props.t("ADD_DYNAMIC_QUESTION_MODAL_TITLE")}
        </ModalHeader>
        <ModalBody className="d-flex flex-column align-content-center">
          <NonLinearWizard defaultStep={this.props.defaultStep} data={[]}>
            <Step
              component={Step1QuestionType}
              title={this.props.t("ADD_QUESTION_MODAL_STEP_1_TITLE", {
                step: 1,
                totalSteps: this.totalSteps,
              })}
            />
            <Step
              component={Step2HierarchyScope}
              title={this.props.t("ADD_QUESTION_MODAL_STEP_2_TITLE", {
                step: 2,
                totalSteps: this.totalSteps,
              })}
            />
            <Step
              component={Step3ReasonSpecificUsage}
              title={this.props.t("ADD_QUESTION_MODAL_STEP_3_TITLE", {
                step: 3,
                totalSteps: this.totalSteps,
              })}
            />
            <Step
              component={Step4LengthScope}
              title={this.props.t("ADD_QUESTION_MODAL_STEP_4_TITLE", {
                step: 4,
                totalSteps: this.totalSteps,
              })}
              isLastStep={this.totalSteps === 4}
            />
            <Step
              component={AnswerOptions}
              title={this.props.t("ADD_QUESTION_MODAL_STEP_5_TITLE", {
                step: 5,
                totalSteps: this.totalSteps,
              })}
              hidden={this.isAnswerOptionsHidden}
            />
          </NonLinearWizard>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={this.handleCancel}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="save"
            onClick={this.handleSubmitDynamicQuestionDraft}
            disabled={!this.props.isAssignmentsValid}
          >
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    draft: serviceSelectors.getDynamicQuestionDraft(state),
    assignments: serviceSelectors.getDynamicQuestionAssignments(state),
    isAssignmentsValid: serviceSelectors.isDynamicQuestionAssignmentsValid(state),
    schema: serviceSelectors.getDynamicQuestionRevisionSchema(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDynamicQuestionRevisionSchema: actionCreators.SetDynamicQuestionRevisionSchema,
  setShowAddDynamicQuestionModal: actionCreators.SetShowAddDynamicQuestionModal,
  addNewDynamicQuestion: actionCreators.AddNewDynamicQuestion,
  getAbsenceReasons: sharedActionCreators.GetAbsenceReasons,
  getHierarchyTree: sharedActionCreators.GetHierarchyTree,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AddDynamicQuestionWizardModal);
